import React, { useState, useEffect } from "react";
import { api } from "../lib/api";
import { LogIn, ArrowLeft, Check, User } from "lucide-react";
import { OnScreenKeyboard } from "../components/OnScreenKeyboard";

const HOSTS = ["Rocco Boyd", "Glenn Anderson"];

export default function ScreenSignIn() {
  const [step, setStep] = useState("welcome"); // welcome | host | name | done
  const [visiting, setVisiting] = useState("");
  const [otherMode, setOtherMode] = useState(false);
  const [otherHost, setOtherHost] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmName, setConfirmName] = useState("");

  const reset = () => {
    setStep("welcome"); setVisiting(""); setOtherMode(false);
    setOtherHost(""); setName(""); setConfirmName("");
  };

  useEffect(() => {
    if (step !== "done") return;
    const t = setTimeout(reset, 6000);
    return () => clearTimeout(t);
  }, [step]);

  const pickHost = (h) => { setVisiting(h); setOtherMode(false); setStep("name"); };
  const continueOther = () => {
    if (!otherHost.trim()) return;
    setVisiting(otherHost.trim()); setStep("name");
  };

  const signIn = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/visits", { visiting, visitor_name: name.trim() });
      setConfirmName(name.trim());
      setStep("done");
    } catch (e) {
      alert("Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full h-20 rounded-2xl bg-[#09090B] border-2 border-white/10 px-6 text-white text-3xl font-semibold outline-none focus:border-[#FF007F] text-center";

  return (
    <div className="min-h-screen bg-[#09090B] text-white flex flex-col items-center justify-center p-6 relative overflow-hidden select-none">
      <img src="/yaz-wall.webp" alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/75" />
      {/* glow accents */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[32rem] h-[32rem] rounded-full bg-[#FF007F]/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[32rem] h-[32rem] rounded-full bg-[#00F0FF]/20 blur-[120px]" />

      <div className="relative z-10 w-full max-w-4xl text-center">
        {/* Brand */}
        <div className="mb-8">
          <h1 className="font-display font-black text-6xl md:text-7xl tracking-tight leading-none">
            YAZ<span className="text-[#FF007F]">.</span>
          </h1>
          <p className="font-display font-bold text-base md:text-lg tracking-[0.3em] text-[#00F0FF] mt-2">
            BY NEAD
          </p>
        </div>

        {step === "welcome" && (
          <div className="animate-[fadeUp_0.4s_ease]">
            <p className="font-display font-black text-4xl md:text-5xl mb-3">WELCOME TO YAZ</p>
            <p className="text-[#A1A1AA] text-lg md:text-xl mb-12">Please sign in for your visit</p>
            <button
              data-testid="screen-login-button"
              onClick={() => setStep("host")}
              className="mx-auto h-24 px-16 rounded-3xl bg-[#FF007F] text-white font-display font-black text-4xl flex items-center justify-center gap-4 active:scale-95 transition-transform duration-100 shadow-[0_0_40px_rgba(255,0,127,0.4)]"
            >
              <LogIn className="w-10 h-10" /> LOG IN
            </button>
          </div>
        )}

        {step === "host" && (
          <div className="animate-[fadeUp_0.4s_ease]">
            <p className="font-display font-black text-4xl md:text-5xl mb-8">Who are you visiting?</p>
            <div className="grid gap-4">
              {HOSTS.map((h) => (
                <button
                  key={h}
                  data-testid={`host-${h.split(" ")[0].toLowerCase()}`}
                  onClick={() => pickHost(h)}
                  className="h-24 rounded-3xl bg-[#18181B] border-2 border-white/10 hover:border-[#00F0FF] text-white font-display font-black text-3xl active:scale-95 transition-all duration-100"
                >{h}</button>
              ))}

              {!otherMode ? (
                <button
                  data-testid="host-other"
                  onClick={() => setOtherMode(true)}
                  className="h-24 rounded-3xl bg-[#18181B] border-2 border-dashed border-white/20 hover:border-[#FF007F] text-[#A1A1AA] font-display font-black text-3xl active:scale-95 transition-all duration-100"
                >TYPE OTHER HERE</button>
              ) : (
                <div className="flex flex-col gap-4">
                  <input
                    data-testid="host-other-input"
                    readOnly
                    inputMode="none"
                    value={otherHost}
                    placeholder="Who are you visiting?"
                    className={inputCls}
                  />
                  <OnScreenKeyboard value={otherHost} onChange={setOtherHost} />
                  <button
                    data-testid="host-continue"
                    onClick={continueOther}
                    disabled={!otherHost.trim()}
                    className="h-20 rounded-2xl bg-[#00F0FF] text-black font-display font-black text-2xl active:scale-95 transition-transform duration-100 disabled:opacity-40"
                  >CONTINUE</button>
                </div>
              )}
            </div>
            <button
              onClick={reset}
              className="mt-8 mx-auto text-[#71717A] hover:text-white flex items-center gap-2 font-bold"
            ><ArrowLeft className="w-5 h-5" /> Back</button>
          </div>
        )}

        {step === "name" && (
          <div className="animate-[fadeUp_0.4s_ease]">
            <p className="text-[#00F0FF] font-bold text-lg mb-2 flex items-center justify-center gap-2">
              <User className="w-5 h-5" /> Visiting {visiting}
            </p>
            <p className="font-display font-black text-4xl md:text-5xl mb-8">What's your name?</p>
            <input
              data-testid="visitor-name-input"
              readOnly
              inputMode="none"
              value={name}
              placeholder="Type your name"
              className={inputCls + " mb-5"}
            />
            <OnScreenKeyboard value={name} onChange={setName} />
            <button
              data-testid="visitor-signin-button"
              onClick={signIn}
              disabled={!name.trim() || busy}
              className="mt-5 w-full h-24 rounded-3xl bg-[#FF007F] text-white font-display font-black text-4xl flex items-center justify-center gap-4 active:scale-95 transition-transform duration-100 disabled:opacity-40 shadow-[0_0_40px_rgba(255,0,127,0.4)]"
            >
              <Check className="w-10 h-10" /> {busy ? "SIGNING IN…" : "SIGN IN"}
            </button>
            <button
              onClick={() => setStep("host")}
              className="mt-8 mx-auto text-[#71717A] hover:text-white flex items-center gap-2 font-bold"
            ><ArrowLeft className="w-5 h-5" /> Back</button>
          </div>
        )}

        {step === "done" && (
          <div data-testid="screen-confirmation" className="animate-[fadeUp_0.4s_ease]">
            <div className="mx-auto w-28 h-28 rounded-full bg-[#00FA9A] text-black flex items-center justify-center mb-8 shadow-[0_0_50px_rgba(0,250,154,0.5)]">
              <Check className="w-16 h-16" strokeWidth={3} />
            </div>
            <p className="font-display font-black text-5xl md:text-6xl mb-4">Welcome, {confirmName}!</p>
            <p className="text-[#A1A1AA] text-xl mb-10">You're signed in to see {visiting}. Enjoy your visit.</p>
            <button
              data-testid="screen-reset"
              onClick={reset}
              className="mx-auto h-16 px-10 rounded-2xl bg-[#18181B] border border-white/10 text-white font-display font-bold text-xl active:scale-95 transition-transform duration-100"
            >DONE</button>
          </div>
        )}
      </div>
    </div>
  );
}
